import { useAppDispatch, useAppSelector } from "src/shared/hooks/redux";
import { useDeepCompareEffect } from "react-use";
import { AsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "src/shared/store";
import isTest from "../util/is-test";

interface FetchPayload {
  [key: string]: any;
}
interface FetchOptions {
  fetch: AsyncThunk<any, FetchPayload, {}>;
  dataSelector: any;
  statusSelector: (state: RootState) => "pending" | "fulfilled" | "rejected";
}
interface ExtraOptions {
  interval?: number;
  deactivate: boolean;
}

function useFetch<T>(
  fetchOptions: FetchOptions | any,
  params?: any,
  extraOptions?: ExtraOptions | any
): {
  data: T;
  isLoading: boolean;
  fetch: any;
} {
  const { fetch, dataSelector, statusSelector } = fetchOptions;
  const dispatch = useAppDispatch();
  const data = useAppSelector<any>(dataSelector);
  const status = useAppSelector(statusSelector);
  const isLoading = status === "pending";
  const { interval, deactivate } = extraOptions ?? {};
  let timer: any;

  function next() {
    dispatch(fetch(params));
  }

  useDeepCompareEffect(() => {
    if (deactivate) return;

    if (interval && !isTest()) {
      timer = setInterval(() => {
        next();
      }, interval);
    } else {
      next();
    }

    return () => {
      clearInterval(timer);
    };
  }, [dispatch, fetch, params, interval]);

  return {
    data,
    isLoading,
    fetch,
  };
}

export default useFetch;
